import axios from 'axios';
import { ENDPOINTS } from '../constants/endpoints';
import {
    AitAccount, ComputationRequest, CorporateTaxRate, CorporateTaxRateRequest, CorporateTaxSettings,
    CorporateTaxSettingsRequest, CreditCategory, CreditStatus, DeferredRequest, DeferredTax, EvidenceRequest,
    LedgerRow, LoadDefaultsResult, SetOff, SetOffRequest, SuggestedAccountsResult, TaxComputation, TaxCreditLedger,
    TaxPayment, TaxPaymentRequest,
} from '../interfaces/corporateTax.interface';

const E = ENDPOINTS.CORPORATE_TAX;

export const CorporateTaxService = {
    // settings, rates
    getSettings: async (inst: string): Promise<CorporateTaxSettings> => (await axios.get(E.SETTINGS(inst))).data,
    saveSettings: async (inst: string, req: CorporateTaxSettingsRequest): Promise<CorporateTaxSettings> =>
        (await axios.put(E.SETTINGS(inst), req)).data,
    detectAit: async (inst: string): Promise<AitAccount[]> => (await axios.get(E.DETECT_AIT(inst))).data,
    createSuggestedAccounts: async (inst: string): Promise<SuggestedAccountsResult> =>
        (await axios.post(E.SUGGESTED_ACCOUNTS(inst))).data,
    listRates: async (inst: string): Promise<CorporateTaxRate[]> => (await axios.get(E.RATES(inst))).data,
    createRate: async (inst: string, req: CorporateTaxRateRequest): Promise<CorporateTaxRate> =>
        (await axios.post(E.RATES(inst), req)).data,
    updateRate: async (inst: string, id: string, req: CorporateTaxRateRequest): Promise<CorporateTaxRate> =>
        (await axios.put(E.RATE(inst, id), req)).data,
    deleteRate: async (inst: string, id: string): Promise<void> => { await axios.delete(E.RATE(inst, id)); },
    loadDefaults: async (inst: string, incomeYear: number): Promise<LoadDefaultsResult> =>
        (await axios.post(E.LOAD_DEFAULTS(inst), { incomeYear })).data,

    // credit ledger
    ledger: async (inst: string, incomeYear: number, category?: CreditCategory | '', status?: CreditStatus | ''): Promise<TaxCreditLedger> =>
        (await axios.get(E.LEDGER(inst), { params: { incomeYear, category: category || undefined, status: status || undefined } })).data,
    saveEvidence: async (inst: string, lineId: string, req: EvidenceRequest): Promise<LedgerRow> =>
        (await axios.put(E.EVIDENCE(inst, lineId), req)).data,

    // current-tax computation
    listComputations: async (inst: string): Promise<TaxComputation[]> => (await axios.get(E.COMPUTATIONS(inst))).data,
    previewComputation: async (inst: string, req: ComputationRequest): Promise<TaxComputation> =>
        (await axios.post(E.COMPUTATION_PREVIEW(inst), req)).data,
    saveComputation: async (inst: string, req: ComputationRequest, id?: string): Promise<TaxComputation> =>
        (id ? await axios.put(E.COMPUTATION(inst, id), req) : await axios.post(E.COMPUTATIONS(inst), req)).data,
    deleteComputation: async (inst: string, id: string): Promise<void> => { await axios.delete(E.COMPUTATION(inst, id)); },
    postComputation: async (inst: string, id: string): Promise<TaxComputation> =>
        (await axios.post(E.COMPUTATION_POST(inst, id))).data,
    reverseComputation: async (inst: string, id: string): Promise<TaxComputation> =>
        (await axios.post(E.COMPUTATION_REVERSE(inst, id))).data,

    // deferred tax
    listDeferred: async (inst: string): Promise<DeferredTax[]> => (await axios.get(E.DEFERRED_LIST(inst))).data,
    previewDeferred: async (inst: string, req: DeferredRequest): Promise<DeferredTax> =>
        (await axios.post(E.DEFERRED_PREVIEW(inst), req)).data,
    saveDeferred: async (inst: string, req: DeferredRequest, id?: string): Promise<DeferredTax> =>
        (id ? await axios.put(E.DEFERRED(inst, id), req) : await axios.post(E.DEFERRED_LIST(inst), req)).data,
    deleteDeferred: async (inst: string, id: string): Promise<void> => { await axios.delete(E.DEFERRED(inst, id)); },
    postDeferred: async (inst: string, id: string): Promise<DeferredTax> => (await axios.post(E.DEFERRED_POST(inst, id))).data,
    reverseDeferred: async (inst: string, id: string): Promise<DeferredTax> => (await axios.post(E.DEFERRED_REVERSE(inst, id))).data,

    // payments and set-off
    listPayments: async (inst: string): Promise<TaxPayment[]> => (await axios.get(E.PAYMENTS(inst))).data,
    createPayment: async (inst: string, req: TaxPaymentRequest): Promise<TaxPayment> => (await axios.post(E.PAYMENTS(inst), req)).data,
    reversePayment: async (inst: string, id: string): Promise<TaxPayment> => (await axios.post(E.PAYMENT_REVERSE(inst, id))).data,
    listSetOffs: async (inst: string): Promise<SetOff[]> => (await axios.get(E.SET_OFFS(inst))).data,
    createSetOff: async (inst: string, req: SetOffRequest): Promise<SetOff> => (await axios.post(E.SET_OFFS(inst), req)).data,
    reverseSetOff: async (inst: string, id: string): Promise<SetOff> => (await axios.post(E.SET_OFF_REVERSE(inst, id))).data,
};
